import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { IAffectedPerson } from 'app/models/compliance/affected-person';
import { IComplaint } from 'app/models/compliance/complaint';
import { ComplaintService } from 'app/services/compliance/complaint.service';
import { IIHCAgency } from 'app/models/compliance/ihc-agency';
import { AffectedPersonComponent } from '../affected-person/affected-person.component';
import { SharedService } from '@shared/services/shared.service';
import { trigger, transition, style, animate } from '@angular/animations';
import { ActivatedRoute } from '@angular/router';
import { PublicDocumentComponent } from '../../public-document/public-document.component';

@Component({
  selector: 'app-complaint',
  standalone: false,
  templateUrl: './complaint.component.html',
  styleUrl: './complaint.component.scss',
  animations: [
    trigger('slideLeft', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(-100%)' }))
      ])
    ]),
    trigger('slideRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ]
})

export class ComplaintComponent implements OnInit {
  @ViewChild(AffectedPersonComponent) affectedPersonComponent!: AffectedPersonComponent;
  @ViewChild(PublicDocumentComponent) documentComponent!: PublicDocumentComponent;

  public titleFull = "Health Facility Licensing and Certification";
  public titleTablet = "Health Facility Licensing and Certification";
  public titleMobile = "HFLC";

  public complaintId: number = 0;
  public clientId: string = '';
  public updatingClientNarrative: boolean = false;
  public displayStart: boolean = false;
  public clientNarrative: string = '';
  public submitting: boolean = false;

  public complaint: IComplaint = {
    id: 0,
    complaintId: '',
    entity: '',
    otherEntity: '',
    entityId: 0,
    entityGuid: '',
    firstName: '',
    middleName: '',
    lastName: '',
    phone: '',
    email: '',
    complainantIsClient: null,
    complainantAddress: '',
    employeeStatus: '',
    neverAnEmployee: null,
    workForAPS: null,
    apsAgency: '',
    description: '',
    incidentDates: '',
    summary: '',
    summaryOther: '',
    othersWithKnowledge: '',
    complaintSubmittedAgency: '',
    complaintSubmittedOther: '',
    complaintSubmittedPolice: '',
    unlicensedAgency: false,
    affectedPeople: []
  };

  public affectedPeople: IAffectedPerson[] = [];
  public ihcAgencies: IIHCAgency[] = [];
  public showSuccess: boolean = false;
  public showError: boolean = false;
  public complaintForm = new FormGroup({
    entity: new FormControl(''),
    entityGuid: new FormControl(''),
    entityId: new FormControl(0),
    otherEntity: new FormControl(''),
    firstName: new FormControl(''),
    middleName: new FormControl(''),
    lastName: new FormControl(''),
    phone: new FormControl(''),
    email: new FormControl('', Validators.email),
    complainantIsClient: new FormControl(null),
    complainantAddress: new FormControl(''),
    employeeStatus: new FormControl(''),
    neverAnEmployee: new FormControl(null),
    workForAPS: new FormControl(null),
    apsAgency: new FormControl(''),
    description: new FormControl(''),
    incidentDates: new FormControl(''),
    summary: new FormControl([]),
    summaryOther: new FormControl(''),
    othersWithKnowledge: new FormControl(''),
    complaintSubmittedAgency: new FormControl(''),
    complaintSubmittedOther: new FormControl(''),
    complaintSubmittedPolice: new FormControl(''),
    unlicensedAgency: new FormControl(false)
  });

  public complaintService = inject(ComplaintService);
  public route = inject(ActivatedRoute);
  public sharedService = inject(SharedService);

  constructor() {
    this.complaintId = Number(this.route.snapshot.paramMap.get('complaintId'));
    this.clientId = this.route.snapshot.paramMap.get('clientId') || '';
  }

  ngOnInit(): void {
    if(this.complaintId > 0){
      this.complaintService.getComplaintByClient(this.complaintId, this.clientId).subscribe({
        next: (complaint: IComplaint) => {
          this.complaint = complaint;
        },
        error: (error) => {
          console.error('Error loading complaint:', error);
        },
        complete: () => {
          this.updatingClientNarrative = true;
          this.clientNarrative = this.complaint.affectedPeople[0]?.clientNarrative || '';
        }
      });
    }
    else{
      this.displayStart = true;
    }
  }

  confirmNewComplaint(): void {
    this.sharedService.openConfirm('Start a complaint for an in home care agency?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.createComplaint();
        }
      }
    );
  }

  createComplaint(): void {
    this.complaintService.createComplaint().subscribe({
      next: (complaint: IComplaint) => {
        this.complaint = complaint;
      },
      error: (error) => {
        console.error('Error creating complaint:', error);
      },
      complete: () => this.loadIhcAgencies()
    });
  }

  loadIhcAgencies(): void {
    this.complaintService.getIhcAgencies().subscribe({
      next: (agencies: IIHCAgency[]) => {
        this.ihcAgencies = agencies;
      },
      error: (error) => {
        console.error('Error loading IHC agencies:', error);
      }
    });
  }

  onSummaryChange(selectedValues: string[]): void {
    this.complaint.summary = JSON.stringify(selectedValues || []);
  }

  getSummaryArray(): string[] {
    try {
      return this.complaint.summary ? JSON.parse(this.complaint.summary) : [];
    } catch {
      return [];
    }
  }

  selectAgency(entity: string): void {
    if(entity === 'Other'){
      this.complaintForm.patchValue({ entityGuid: '', entityId: 0, unlicensedAgency: true });
      return;
    }
    let agency: IIHCAgency = this.ihcAgencies.find(a => a.agencyName === entity)!;
    if (agency) {
      this.complaintForm.patchValue({ entityGuid: agency.agencyGuid, entityId: agency.agencyId, unlicensedAgency: false });
    } else {
      this.complaintForm.patchValue({ entityGuid: '', entityId: 0, unlicensedAgency: true });
    }
  }

  getEmptyComplaint(): IComplaint {
    let emptyComplaint: IComplaint = {
      id: 0,
      complaintId: '',
      entity: '',
      otherEntity: '',
      entityId: 0,
      entityGuid: '',
      firstName: '',
      middleName: '',
      lastName: '',
      phone: '',
      email: '',
      complainantIsClient: null,
      complainantAddress: '',
      employeeStatus: '',
      neverAnEmployee: null,
      workForAPS: null,
      apsAgency: '',
      incidentDates: '',
      description: '',
      summary: '',
      summaryOther: '',
      othersWithKnowledge: '',
      complaintSubmittedAgency: '',
      complaintSubmittedOther: '',
      complaintSubmittedPolice: '',
      unlicensedAgency: false,
      affectedPeople: []
    }

    return emptyComplaint;
  }

  submitDocuments(): void {
    this.submitting = true;
    this.documentComponent.uploadFiles();
  }

  submitComplaint(): void {
    let complaintFormValue = this.complaintForm.value;
    this.complaint.entity = complaintFormValue.entity || '';
    this.complaint.otherEntity = complaintFormValue.otherEntity || '';
    this.complaint.entityId = complaintFormValue.entityId || 0;
    this.complaint.entityGuid = complaintFormValue.entityGuid || '';
    this.complaint.firstName = complaintFormValue.firstName || '';
    this.complaint.middleName = complaintFormValue.middleName || '';
    this.complaint.lastName = complaintFormValue.lastName || '';
    this.complaint.phone = complaintFormValue.phone || '';
    this.complaint.email = complaintFormValue.email || '';
    this.complaint.complainantIsClient = complaintFormValue.complainantIsClient || null;
    this.complaint.complainantAddress = complaintFormValue.complainantAddress || '';
    this.complaint.employeeStatus = complaintFormValue.employeeStatus || '';
    this.complaint.neverAnEmployee = complaintFormValue.neverAnEmployee || false;
    this.complaint.workForAPS = complaintFormValue.workForAPS || false;
    this.complaint.apsAgency = complaintFormValue.apsAgency || '';
    this.complaint.incidentDates = complaintFormValue.incidentDates || '';
    this.complaint.description = complaintFormValue.description || '';
    this.complaint.summaryOther = complaintFormValue.summaryOther || '';
    this.complaint.othersWithKnowledge = complaintFormValue.othersWithKnowledge || '';
    this.complaint.complaintSubmittedAgency = complaintFormValue.complaintSubmittedAgency || '';
    this.complaint.complaintSubmittedOther = complaintFormValue.complaintSubmittedOther || '';
    this.complaint.complaintSubmittedPolice = complaintFormValue.complaintSubmittedPolice || '';

    let formValue = this.affectedPersonComponent.affectedPersonForm.getRawValue();
    let affectedPerson: IAffectedPerson = {
      firstName: formValue.firstName,
      middleName: formValue.middleName,
      lastName: formValue.lastName,
      address: formValue.address,
      phone: formValue.phone,
      email: formValue.email,
      dob: formValue.dob?.toDateString() || null,
      gender: formValue.gender,
      relationship: formValue.relationship,
      relationshipOther: formValue.relationshipOther,
      canContact: formValue.canContact,
      whyNoContact: formValue.whyNoContact,
      otherContactReason: this.affectedPersonComponent.otherContactReason,
      clientNarrative: '',
      incidentLocation: '',
      servicesReceived: formValue.servicesReceived,
      clientSchedule: formValue.clientSchedule,
      dateAdmitted: formValue.dateAdmitted?.toDateString() || null,
      currentPatient: formValue.currentPatient,
      dateDischarged: formValue.dateDischarged?.toDateString() || null
    };

    this.affectedPeople.push(affectedPerson);
    this.affectedPeople.push(...this.affectedPersonComponent.affectedPeople);

    this.complaint.affectedPeople = this.affectedPeople;
    this.complaintService.submitComplaint(this.complaint).subscribe({
      next: (result: boolean) => this.showSuccess = result,
      error: (error) => {
        this.showError = true;
        this.submitting = false;
      },
      complete: () => {
        this.complaintForm.reset();
        this.complaint = this.getEmptyComplaint();
        this.affectedPeople = [];
        this.submitting = false;
      }
    })
  };

  saveClientNarrative(): void {
    this.complaintService.updateClientNarrative(this.complaint.id, this.clientId, this.clientNarrative).subscribe({
      next: (result: boolean) => {
        if(result){
          this.sharedService.toastr.success('Update successful.');
        }},
      error: (error) => {
        console.error('Error updating client narrative:', error);
      },
      complete: () => {
        this.complaint = this.getEmptyComplaint();
        this.showSuccess = true;
      }
    });
  }

  cancelComplaint(): void {
    this.sharedService.openConfirm('Cancel submitting the complaint? All data will be lost.');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.deleteComplaint();
        }
      }
    );
  }

  deleteComplaint(): void {
    this.complaintService.deleteComplaint(this.complaint.id).subscribe({
      complete: () => {
        this.complaintForm.reset();
        this.complaint = this.getEmptyComplaint();
        this.affectedPeople = [];
        this.showSuccess = false;
      }
    });
  }

}
